import React, { useState, useEffect } from 'react';
import { useApi } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from './ui/Button';

interface Props {
  children: React.ReactNode;
}

const RequireOrganization: React.FC<Props> = ({ children }) => {
  const { user } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const [hasOrganization, setHasOrganization] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [orgWebsite, setOrgWebsite] = useState('');

  useEffect(() => {
    const checkOrganization = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const profile = await api.getUserProfile();
        setHasOrganization(!!profile.organization_id);
      } catch (err) {
        console.error('Error checking organization:', err);
        setHasOrganization(false);
      } finally {
        setLoading(false);
      }
    };

    checkOrganization();
  }, [user, api]);

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email) return;
    
    setCreatingOrg(true);
    setError(null);

    try {
      await api.createOrganization({
        name: orgName,
        website: orgWebsite,
        createdBy: user.id || user.email,
      });

      // Refresh the page to update the user's organization status
      window.location.reload();
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setError(err.message || 'Failed to create organization');
    } finally {
      setCreatingOrg(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!hasOrganization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Create Your Organization
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              You need to create an organization before you can manage tasks and users.
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleCreateOrganization}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-800">{error}</div>
              </div>
            )}

            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="org-name" className="sr-only">
                  Organization Name
                </label>
                <input
                  id="org-name"
                  name="name"
                  type="text"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Organization Name"
                />
              </div>
              <div>
                <label htmlFor="org-website" className="sr-only">
                  Website (optional)
                </label>
                <input
                  id="org-website"
                  name="website"
                  type="url"
                  value={orgWebsite}
                  onChange={(e) => setOrgWebsite(e.target.value)}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Website (optional)"
                />
              </div>
            </div>

            <div>
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={creatingOrg || !orgName}
              >
                {creatingOrg ? 'Creating...' : 'Create Organization'}
              </Button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Already have an organization?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/support')}
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Contact support
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RequireOrganization; 